import {
  type AdeaT,
  catchUp,
  caughtUp,
  copyLoaded,
  createFormat,
  createWebI18n,
  type Formatter,
  groupsForPath,
  type LiveLocale,
  loadCopy,
  loadGroups,
  NAMESPACE,
  readIn,
  resolveLocale,
} from "@adea/i18n/web";
import { type BlocksCopyIn, BlocksProvider } from "@adea/ui";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  Suspense,
  use,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { meQuery } from "./session.ts";

interface I18nValue {
  locale: LiveLocale;
  t: AdeaT;
  format: Formatter;
  /** How many pieces of copy have arrived since the page opened: a new value draws everything that reads copy. */
  arrivals: number;
}

const I18nContext = createContext<I18nValue | null>(null);

const browserLocale = resolveLocale({ acceptLanguage: navigator.languages.join(",") });
// In development, a key read before its piece arrived says so: the address should fetch that piece (`ALSO` in
// packages/i18n/src/paths.ts).
const warned = new Set<string>();
const i18n = createWebI18n(
  browserLocale,
  import.meta.env.DEV
    ? {
        onLateKey: (key, group) => {
          const once = `${location.pathname} ${group}`;
          if (warned.has(once)) return;
          warned.add(once);
          console.warn(
            `copy: "${key}" was read on ${location.pathname} before its piece "${group}" arrived, so it showed blank for a moment. If it shows without a click, fetch "${group}" on this address (ALSO in packages/i18n/src/paths.ts).`,
          );
        },
      }
    : {},
);

/** Starts fetching the browser's language at once, in parallel with the first screen's code (`main.tsx`). */
export const startCopy = (): void => void loadCopy(i18n, browserLocale);

/**
 * Starts fetching the copy of the screens at these addresses (`/questions/…`): called when a navigation starts or a
 * link is hovered, so it travels with the screen's code. A screen that asks for words before they arrive shows
 * nothing in their place until they do (see `createWebI18n`), so this is only about not waiting.
 */
export const warmCopy = (path: string): void => {
  const groups = groupsForPath(path.split("/"));
  if (groups.length > 0) void loadGroups(i18n, currentLocale, groups);
};

/**
 * The copy of the screens at an address, as a promise: for a piece that shows over any page (the connect panel
 * shows Data's and code's words on Home), so it is drawn with its words rather than blank and then filled.
 */
export const loadCopyFor = (path: string): Promise<void> =>
  loadGroups(i18n, currentLocale, groupsForPath(path.split("/")));

/** The language every action call is made in (refusals come back in it). Set by the provider. */
let currentLocale: LiveLocale = browserLocale;
export const callLocale = (): LiveLocale => currentLocale;

/**
 * Copy in one language, with its number and date formats: the language's base and the screens' pieces already
 * fetched in another language; a screen's own piece arrives with its code (`warmCopy`).
 */
export function LocaleProvider({ locale, children }: { locale: LiveLocale; children: ReactNode }) {
  return (
    <Suspense fallback={null}>
      <CopyGate locale={locale}>{children}</CopyGate>
    </Suspense>
  );
}

/**
 * Waits for the language's copy (the Suspense boundary above shows nothing meanwhile). It has no hooks of its own:
 * a component that suspends with `use` and then calls hooks is replayed by React with a different number of hooks.
 */
function CopyGate({ locale, children }: { locale: LiveLocale; children: ReactNode }) {
  if (!copyLoaded(i18n, locale) || !caughtUp(i18n, locale))
    // the language's base, and the screens' pieces already fetched in another language (the browser's, or the one
    // before a switch): without them those screens would read in English
    use(Promise.all([loadCopy(i18n, locale), catchUp(i18n, locale)]));
  return <LoadedLocale locale={locale}>{children}</LoadedLocale>;
}

/** Offers a language whose copy has arrived. */
function LoadedLocale({ locale, children }: { locale: LiveLocale; children: ReactNode }) {
  readIn(i18n, locale);
  // A screen's copy that arrives later draws everything that reads copy again. i18next raises "added" on its store,
  // not on the instance (listening on the instance never fires, and a screen whose piece came late stayed blank).
  // A piece that landed between the first draw and this effect is caught by drawing once more.
  const [arrivals, setArrivals] = useState(0);
  useEffect(() => {
    const again = () => setArrivals((n) => n + 1);
    i18n.store.on("added", again);
    again();
    return () => i18n.store.off("added", again);
  }, []);
  const value = useMemo<I18nValue>(
    () => ({ locale, t: i18n.getFixedT(locale, NAMESPACE), format: createFormat(locale), arrivals }),
    [locale, arrivals],
  );
  // blocks and the thread view read their words and formats from here, wherever they show
  const blocks = useMemo(
    () =>
      ({
        blocks: value.t("blocks", { returnObjects: true }),
        thread: value.t("thread", { returnObjects: true }),
      }) as BlocksCopyIn,
    [value],
  );
  return (
    <I18nContext.Provider value={value}>
      <BlocksProvider copy={blocks} format={value.format}>
        {children}
      </BlocksProvider>
    </I18nContext.Provider>
  );
}

/**
 * One i18next instance per tab. The language is the person's (from `/api/v1/me`, which applies their
 * profile choice, the browser and the company's default in that order); before sign-in, the browser's.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const { data: me } = useQuery({ ...meQuery, enabled: false });
  const locale = me?.locale ?? browserLocale;
  currentLocale = locale;

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return <LocaleProvider locale={locale}>{children}</LocaleProvider>;
}

function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("I18nProvider is missing");
  return value;
}

export const useT = (): AdeaT => useI18n().t;
export const useFormat = (): Formatter => useI18n().format;
export const useLocale = (): LiveLocale => useI18n().locale;
